import { useEffect, useState } from "react";

const SPECIALTY_STORAGE_KEY = "biosynciq_practice_specialty";
const ASSIGNMENTS_STORAGE_KEY = "biosynciq_oncology_assignments";
const SPECIALTY_CHANGE_EVENT = "biosynciq-practice-specialty-change";
const SIGNUP_SKIP_SPECIALTY_KEY = "biosynciq_skip_specialty_after_signup";
export const ASSIGNMENT_CHANGE_EVENT = "biosynciq-oncology-assignment-change";

export const PRACTICE_SPECIALTIES = [
  { value: "cardiology", label: "Cardiology" },
  { value: "anesthesiology", label: "Anesthesiology" },
  {
    value: "interventional_pain_management",
    label: "Interventional Pain Management",
  },
  { value: "neurosurgery", label: "Neurosurgery" },
  { value: "orthopedic_surgery", label: "Orthopedic Surgery" },
  { value: "pain_management", label: "Pain Management" },
  {
    value: "physical_medicine_and_rehabilitation",
    label: "Physical Medicine and Rehabilitation",
  },
  { value: "oncology", label: "Oncology" },
];

export const SELECTABLE_PRACTICE_SPECIALTIES = [
  { value: "oncology", label: "Oncology" },
  { value: "pain_management", label: "Pain Management" },
];

export const ONCOLOGY_TREATMENT_SETS = [
  { id: "ablation_embolization", label: "Ablation & Embolization" },
  { id: "cellular_therapy", label: "Cellular Therapy" },
  { id: "chemotherapy", label: "Chemotherapy" },
  { id: "clinical_trial", label: "Clinical Trial Participation" },
  { id: "hormone_therapy", label: "Hormone Therapy" },
  { id: "immunotherapy", label: "Immunotherapy" },
  { id: "oral_anticancer", label: "Oral Anticancer Therapy" },
  { id: "radiation_therapy", label: "Radiation Therapy" },
  { id: "stem_cell_transplant", label: "Stem Cell / Bone Marrow Transplant" },
  { id: "targeted_therapy", label: "Targeted Therapy" },
];

export const CORE_ONCOLOGY_SET = {
  id: "core_oncology",
  label: "Core Oncology and Recovery",
  locked: true,
};

export const RADIATION_SITES = [
  { id: "head_neck", label: "Head and Neck" },
  { id: "brain", label: "Brain" },
  { id: "chest_thoracic", label: "Chest and Thoracic" },
  { id: "abdominal_pelvic", label: "Abdominal and Pelvic" },
  { id: "breast_regional_nodal", label: "Breast and Regional Nodal" },
];

export const RADIATION_THERAPY_ID = "radiation_therapy";

export function specialtyNameToValue(name) {
  if (name == null || name === "") return null;

  const trimmed = String(name).trim();
  const byValue = PRACTICE_SPECIALTIES.find(
    (item) => item.value === trimmed || item.value === trimmed.toLowerCase()
  );
  if (byValue) return byValue.value;

  const byLabel = PRACTICE_SPECIALTIES.find(
    (item) => item.label.toLowerCase() === trimmed.toLowerCase()
  );
  return byLabel?.value || null;
}

export function getSpecialtyFromProfile(profile) {
  if (!profile) return null;

  const specialty =
    profile.specialty ||
    profile.agent?.specialty ||
    profile.data?.specialty;

  if (!specialty) return null;

  const name = typeof specialty === "string" ? specialty : specialty.name;
  return specialtyNameToValue(name);
}

export function hydratePracticeSpecialtyFromProfile(profile) {
  const existing = getPracticeSpecialty();
  if (existing) return existing;

  const value = getSpecialtyFromProfile(profile);
  if (value) {
    setPracticeSpecialty(value);
  }
  return value;
}

export function getPracticeSpecialty() {
  try {
    return localStorage.getItem(SPECIALTY_STORAGE_KEY);
  } catch (error) {
    return null;
  }
}

export function markSpecialtySelectedDuringSignup() {
  try {
    sessionStorage.setItem(SIGNUP_SKIP_SPECIALTY_KEY, "1");
  } catch (error) {
    // Ignore storage failures; login will still require specialty selection.
  }
}

export function shouldSkipSpecialtySelect() {
  try {
    return sessionStorage.getItem(SIGNUP_SKIP_SPECIALTY_KEY) === "1";
  } catch (error) {
    return false;
  }
}

export function getPracticeSpecialtyLabel(specialty = getPracticeSpecialty()) {
  return (
    PRACTICE_SPECIALTIES.find((item) => item.value === specialty)?.label || ""
  );
}

export function setPracticeSpecialty(value) {
  localStorage.setItem(SPECIALTY_STORAGE_KEY, value);
  window.dispatchEvent(new Event(SPECIALTY_CHANGE_EVENT));
}

export function clearPracticeSpecialty() {
  localStorage.removeItem(SPECIALTY_STORAGE_KEY);
  try {
    sessionStorage.removeItem(SIGNUP_SKIP_SPECIALTY_KEY);
  } catch (error) {
    // Ignore storage failures.
  }
  window.dispatchEvent(new Event(SPECIALTY_CHANGE_EVENT));
}

export function isOncologySpecialty(specialty = getPracticeSpecialty()) {
  return specialty === "oncology";
}

export function isPainManagementSpecialty(specialty = getPracticeSpecialty()) {
  return (
    specialty === "pain_management" ||
    specialty === "interventional_pain_management"
  );
}

export function usePracticeSpecialty() {
  const [specialty, setSpecialty] = useState(getPracticeSpecialty);

  useEffect(() => {
    const sync = () => setSpecialty(getPracticeSpecialty());
    window.addEventListener(SPECIALTY_CHANGE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(SPECIALTY_CHANGE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  return specialty;
}

export function getPatientOncologyAssignment(patientId) {
  if (!patientId) {
    return { sets: [], radiationSites: [] };
  }

  try {
    const all = JSON.parse(localStorage.getItem(ASSIGNMENTS_STORAGE_KEY) || "{}");
    const saved = all[String(patientId)];
    if (!saved) {
      return { sets: [], radiationSites: [] };
    }
    return {
      sets: Array.isArray(saved.sets) ? saved.sets : [],
      radiationSites: Array.isArray(saved.radiationSites)
        ? saved.radiationSites
        : [],
    };
  } catch (error) {
    return { sets: [], radiationSites: [] };
  }
}

export function savePatientOncologyAssignment(patientId, assignment) {
  if (!patientId) return;

  const all = JSON.parse(localStorage.getItem(ASSIGNMENTS_STORAGE_KEY) || "{}");
  all[String(patientId)] = {
    sets: assignment.sets || [],
    radiationSites: assignment.radiationSites || [],
    updatedAt: new Date().toISOString(),
  };
  localStorage.setItem(ASSIGNMENTS_STORAGE_KEY, JSON.stringify(all));
  window.dispatchEvent(new Event(ASSIGNMENT_CHANGE_EVENT));
}

export function buildOncologyAssignmentNotes(sets = [], sites = []) {
  const treatmentLabels = sets.map((set) => set.label).filter(Boolean);
  const siteLabels = sites.map((site) => site.label).filter(Boolean);

  if (!treatmentLabels.length && !siteLabels.length) return "";
  if (!siteLabels.length) return treatmentLabels.join(", ");
  return `${treatmentLabels.join(", ")}; radiation sites: ${siteLabels.join(
    ", "
  )}`;
}

export function normalizeOncologyAssignment(assignment = {}) {
  let sets = Array.isArray(assignment.sets) ? [...assignment.sets] : [];
  let radiationSites = Array.isArray(assignment.radiationSites)
    ? [...assignment.radiationSites]
    : [];

  const hasRadiation = sets.some((set) => set.id === RADIATION_THERAPY_ID);
  if (hasRadiation && !radiationSites.length) {
    sets = sets.filter((set) => set.id !== RADIATION_THERAPY_ID);
  }
  if (!hasRadiation) {
    radiationSites = [];
  }

  return { sets, radiationSites };
}

export function buildOncologyAssignmentCreatePayload({
  patientId,
  agencyId,
  assignment,
}) {
  const { sets, radiationSites } = normalizeOncologyAssignment(assignment);

  return {
    patient_id: Number(patientId),
    agency_id: Number(agencyId),
    assessment_codes: sets.map((set) => set.id).filter(Boolean),
    radiation_site_codes: radiationSites.map((site) => site.id).filter(Boolean),
    notes: buildOncologyAssignmentNotes(sets, radiationSites),
  };
}
